import PropTypes from 'prop-types';
import React from 'react';
import { noop } from 'lodash';

import { LocaleService, WindowProxy } from '@sm360/widget-commons-core-services';
import { InputDropDown } from '@sm360/widget-commons-forms';

import PaymentOptionsModel from 'commons-vehicle/Vehicle/models/PaymentOptionsModel.js';
import TradeInVehicleModel from 'commons-vehicle/Vehicle/models/TradeInVehicleModel.js';

import InitialCashDown from 'commons-vehicle/Vehicle/components/PaymentOptionFilters/InitialCashDown/InitialCashDown.jsx';
import MethodButton from 'commons-vehicle/Vehicle/components/PaymentOptionFilters/MethodButton/MethodButton.jsx';
import PaymentRange from 'commons-vehicle/Vehicle/components/PaymentOptionFilters/PaymentRange/PaymentRange.jsx';
import TradeInValue from 'commons-vehicle/Vehicle/components/PaymentOptionFilters/TradeInValue/TradeInValue.jsx';

import 'commons-vehicle/Vehicle/components/PaymentOptionFilters/PaymentOptionFilters.scss';
import { AnalyticsService, TRACKING_TAGS as TAGS } from '@sm360/widget-commons-general';

const gaTrigger = AnalyticsService.gaTrigger.bind(AnalyticsService);

const possibleFrequency = {
    CASHDOWN: 'cashdown',
    PAYMENT: 'payment',
};
class PaymentOptionFilters extends React.Component {
    constructor(props) {
        super(props);

        const {
            initialTargetPayment,
            maximumTerm,
            preferredPaymentFrequency,
            purchaseMethod,
        } = this.props.paymentOptions;
        const isEmptyPayment = initialTargetPayment > 0;

        this.state = {
            paymentOpened: false,
            currentMethod: purchaseMethod,
            purchaseMethodSort: {
                value: preferredPaymentFrequency,
                label: preferredPaymentFrequency,
            },
            paymentTypeSelection: {
                value: isEmptyPayment ? possibleFrequency.PAYMENT : possibleFrequency.CASHDOWN,
                label: isEmptyPayment
                    ? LocaleService.t('FILTERS.INITIAL_PAYMENT')
                    : LocaleService.t('FILTERS.INITIAL_CASHDOWN'),
            },
            selectedTerms: {
                value: maximumTerm,
                label: maximumTerm,
            },
        };

        this.handleCollapse = this.handleCollapse.bind(this);
        this.handleInputChange = this.handleInputChange.bind(this);
        this.handlePaymentChange = this.handlePaymentChange.bind(this);
        this.openCashdown = this.openCashdown.bind(this);
        this.openPayment = this.openPayment.bind(this);
        this.selectMethod = this.handleMethodSelection.bind(this);
        this.handleMethodSelection = this.handleMethodSelection.bind(this);
    }

    componentWillReceiveProps(newProps) {
        if (
            this.props.hasLease !== newProps.hasLease ||
            this.props.paymentOptions !== newProps.paymentOptions ||
            this.props.tradeInVehicle !== newProps.tradeInVehicle
        ) {
            const {
                maximumTerm,
                initialTargetPayment,
                preferredPaymentFrequency,
                purchaseMethod,
            } = newProps.paymentOptions;
            const isEmptyPayment = initialTargetPayment > 0;
            this.setState({
                currentMethod: purchaseMethod,
                purchaseMethodSort: {
                    value: preferredPaymentFrequency,
                    label: preferredPaymentFrequency,
                },
                paymentTypeSelection: {
                    value: isEmptyPayment ? possibleFrequency.PAYMENT : possibleFrequency.CASHDOWN,
                    label: isEmptyPayment
                        ? LocaleService.t('FILTERS.INITIAL_PAYMENT')
                        : LocaleService.t('FILTERS.INITIAL_CASHDOWN'),
                },
                selectedTerms: {
                    value: maximumTerm,
                    label: maximumTerm,
                },
            });
        }
    }

    openCashdown() {
        if (this.state.paymentTypeSelection.value === possibleFrequency.CASHDOWN) {
            this.props.actions.setTargetPayment(0);
            this.props.actions.setCashDown(0);
        }
    }

    openPayment() {
        gaTrigger(TAGS.TARGET_PAYMENT('click'));
        if (this.state.paymentTypeSelection.value === possibleFrequency.PAYMENT) {
            this.props.actions.setCashDown(0);
        }
    }

    handleCollapse() {
        const windowProxy = WindowProxy.get();
        this.setState(
            (oldState) => {
                return {
                    paymentOpened: !oldState.paymentOpened,
                };
            },
            () => {
                if (windowProxy) {
                    windowProxy.document.body.classList.toggle('paymentPanelOpen-no-scroll', this.state.paymentOpened);
                }
            }
        );
    }

    handleInputChange(inputValue, name) {
        let inputs;
        if (name === 'purchaseMethodSort') {
            inputs = this.state.purchaseMethodSort;
        } else if (name === 'selectTerms') {
            inputs = this.state.selectedTerms;
        }
        if (inputs.value !== inputValue.value) {
            inputs.value = inputValue.value;
            inputs.label = inputValue.label;

            if (name === 'purchaseMethodSort') {
                this.props.actions.selectPreferredPaymentFrequency(inputValue.value);
            } else if (name === 'selectTerms') {
                this.props.actions.selectMaximumTerm(inputValue.value);
            }
        }
    }

    handlePaymentChange(dropdownValue) {
        this.setState(
            {
                paymentTypeSelection: dropdownValue,
            },
            () => {
                if (dropdownValue.value === possibleFrequency.CASHDOWN) {
                    this.openCashdown();
                } else if (dropdownValue.value === possibleFrequency.PAYMENT) {
                    this.openPayment();
                }
            }
        );
    }

    handleMethodSelection(paymentMethod) {
        gaTrigger(TAGS.PAYMENT_TYPE(paymentMethod));
        this.setState({
            currentMethod: paymentMethod,
        });
    }

    render() {
        const { paymentOpened, purchaseMethodSort, selectedTerms } = this.state;
        const emptyFrequencies = this.props.paymentOptions.frequencyValues?.length === 0;
        const emptyTerms = this.props.paymentOptions.terms?.length === 0;

        const paymentFrequenciesButtons = emptyFrequencies
            ? [
                  {
                      value: purchaseMethodSort.value,
                      label: LocaleService.t('FILTERS.NOT_AVAILABLE'),
                  },
              ]
            : this.props.paymentOptions.frequencyValues.map((paymentFrequency) => ({
                  value: paymentFrequency.value,
                  label: paymentFrequency.label,
              }));

        const paymentTypeOptions = [
            {
                value: possibleFrequency.CASHDOWN,
                label: LocaleService.t('FILTERS.INITIAL_CASHDOWN'),
            },
            {
                value: possibleFrequency.PAYMENT,
                label: LocaleService.t('FILTERS.INITIAL_PAYMENT'),
            },
        ];

        const termsButtons = emptyTerms
            ? [{ value: selectedTerms.value, label: LocaleService.t('FILTERS.NOT_AVAILABLE') }]
            : this.props.paymentOptions.terms.map((term) => ({
                  value: term,
                  label: LocaleService.t('MONTH_TERMS', { terms: term }),
              }));

        const trackTradeInValueClick = () => gaTrigger(TAGS.EXCHANGE_VALUE__USED_CAR(WindowProxy.location().pathname));
        const trackOweOnTradeClick = () => gaTrigger(TAGS.OWE_ON_TRADE(WindowProxy.location().pathname));
        const trackInitialCashDownClick = () => gaTrigger(TAGS.INITIAL_CASH_DOWN());
        const trackTargetPaymentChange = (value) => gaTrigger(TAGS.TARGET_PAYMENT('set', value));
        const trackPreferredFrequencyClick = () => gaTrigger(TAGS.PREFERRED_FREQUENCY());
        const trackMaximumTermClick = () => gaTrigger(TAGS.MAXIMUM_TERM());

        const paymentClass = ['listing-used-payment-wrap', this.state.currentMethod];
        const paymentBloc = (
            <div className={paymentClass.join(' ')}>
                <button
                    className="listing-used-filters-head-mobile-close picto-showroom icon-X-rouge"
                    onClick={this.handleCollapse}
                />
                <MethodButton
                    selectPreferredPurchaseMethod={this.props.actions.selectPreferredPurchaseMethod}
                    hasLease={this.props.hasLease}
                    currentMethod={this.state.currentMethod}
                    onSelection={this.handleMethodSelection}
                />
                <div className="listing-used-payment-wrap-range">
                    <InputDropDown
                        name="paymentTypeSelect"
                        value={this.state.paymentTypeSelection.value}
                        options={paymentTypeOptions}
                        placeholder={LocaleService.t('FILTERS.PAYMENT_SELECTION')}
                        valueChange={this.handlePaymentChange}
                        classNames={['input-dropdown not-empty payment-selector']}
                    />
                    {this.state.paymentTypeSelection.value === possibleFrequency.CASHDOWN ? (
                        <InitialCashDown
                            initialCashDown={this.props.paymentOptions.initialCashDown}
                            setInitialCashDown={this.props.actions.setInitialCashDown}
                            setCashDown={this.props.actions.setCashDown}
                            onClick={trackInitialCashDownClick}
                            disabled={emptyTerms || emptyFrequencies}
                        />
                    ) : (
                        <PaymentRange
                            paymentRangeBoundaries={this.props.paymentOptions.rangeBoundaries}
                            setPaymentRange={this.props.actions.setPaymentRange}
                            setInitialTargetPayment={this.props.actions.setInitialTargetPayment}
                            setTargetPayment={this.props.actions.setTargetPayment}
                            initialTargetPayment={this.props.paymentOptions.initialTargetPayment}
                            onChange={trackTargetPaymentChange}
                            disabled={emptyTerms || emptyFrequencies}
                        />
                    )}
                </div>
                <div className="listing-used-payment-wrap-range">
                    <div className="listing-used-payment-frequency">
                        <InputDropDown
                            name="purchaseMethodSort"
                            value={this.state.purchaseMethodSort.value}
                            options={paymentFrequenciesButtons}
                            placeholder={LocaleService.t('PREFERRED_FREQUENCY')}
                            valueChange={this.handleInputChange}
                            classNames={['input-dropdown not-empty']}
                            onClick={trackPreferredFrequencyClick}
                            disabled={emptyFrequencies}
                        />
                    </div>

                    <div className="listing-used-max-term">
                        <InputDropDown
                            name="selectTerms"
                            value={this.state.selectedTerms.value}
                            options={termsButtons}
                            placeholder={LocaleService.t('MAXIMUM_TERM')}
                            valueChange={this.handleInputChange}
                            classNames={['input-dropdown not-empty']}
                            onClick={trackMaximumTermClick}
                            disabled={emptyTerms}
                        />
                    </div>
                </div>
                <TradeInValue
                    tradeInVehicle={this.props.tradeInVehicle}
                    setTradeIn={this.props.actions.setTradeIn}
                    setLien={this.props.actions.setLien}
                    setTargetPayment={this.props.actions.setTargetPayment}
                    initialTargetPayment={this.props.paymentOptions.initialTargetPayment}
                    saveTradeInVehicle={this.props.tradeInActions.saveTradeInVehicle}
                    onValueClick={trackTradeInValueClick}
                    onLienClick={trackOweOnTradeClick}
                />
            </div>
        );

        const userPaymentClass = ['listing-used-payment clearfix'];
        if (paymentOpened) {
            userPaymentClass.push('payment-open');
        }

        return (
            <div className={userPaymentClass.join(' ')}>
                <div className="listing-used-set-budget">
                    <button className="picto-showroom-after" onClick={this.handleCollapse}>
                        {LocaleService.t('SET_MY_BUDGET')}
                    </button>
                </div>
                {paymentBloc}
            </div>
        );
    }
}

PaymentOptionFilters.defaultProps = {
    hasLease: false,
    tradeInVehicle: null,
    paymentOptions: new PaymentOptionsModel(),
    actions: {
        filterPayments: noop,
        selectMaximumTerm: noop,
        selectPreferredPaymentFrequency: noop,
        selectPreferredPurchaseMethod: noop,
        selectPurchaseMethod: noop,
        setCashDown: noop,
        setInitialCashDown: noop,
        setInitialTargetPayment: noop,
        setLien: noop,
        setPaymentRange: noop,
        setTargetPayment: noop,
        setTradeIn: noop,
    },
    tradeInActions: {
        saveTradeInVehicle: noop,
    },
};

PaymentOptionFilters.propTypes = {
    actions: PropTypes.shape({
        filterPayments: PropTypes.func.isRequired,
        selectMaximumTerm: PropTypes.func.isRequired,
        selectPreferredPaymentFrequency: PropTypes.func.isRequired,
        selectPreferredPurchaseMethod: PropTypes.func.isRequired,
        selectPurchaseMethod: PropTypes.func.isRequired,
        setCashDown: PropTypes.func.isRequired,
        setInitialCashDown: PropTypes.func.isRequired,
        setInitialTargetPayment: PropTypes.func.isRequired,
        setLien: PropTypes.func.isRequired,
        setPaymentRange: PropTypes.func.isRequired,
        setTargetPayment: PropTypes.func.isRequired,
        setTradeIn: PropTypes.func.isRequired,
    }).isRequired,
    hasLease: PropTypes.bool,
    paymentOptions: PropTypes.instanceOf(PaymentOptionsModel).isRequired,
    tradeInActions: PropTypes.shape({
        saveTradeInVehicle: PropTypes.func.isRequired,
    }).isRequired,
    tradeInVehicle: PropTypes.instanceOf(TradeInVehicleModel),
};

PaymentOptionFilters.displayName = 'PaymentOptionFilters';

export default PaymentOptionFilters;
